import React, { useState, useEffect } from "react";
import { Loader, Statistic, Table, Icon, Message } from "semantic-ui-react";
import { groupBy, map, orderBy, size, sumBy } from "lodash";
import moment from "moment";
import { HeaderPage, PrintHeader, PrintTable } from "../../components/Admin";
import { useOrder, usePayment, usePrintOrientation } from "../../hooks";
import { getBusinessToday, getBusinessDaysAgo } from "../../utils/date";
import { getTableLabel } from "../../utils/table";
import { getPaymentMethod } from "../../utils/constants";

// Reportes → Anulaciones y descuentos (ruta /admin/cancellations): datos de
// CONTROL para el dueño. Pedidos cancelados antes de cobrar (OrderCancellation:
// el pedido se borró pero quedó registrado con motivo y quién), ventas cobradas
// que un Admin anuló después (Payment VOID) y descuentos/cortesías aplicados al
// cobrar (Payment.discount_amount).

const money = (value) => `S/ ${Number(value || 0).toFixed(2)}`;
const when = (timestamp) =>
  timestamp ? moment.parseZone(timestamp).format("DD/MM/YYYY HH:mm") : "—";
const price = (row) => Number(row.price || 0);

export function CancellationsReport() {
  const [dateFrom, setDateFrom] = useState(() => getBusinessDaysAgo(30));
  const [dateTo, setDateTo] = useState(getBusinessToday);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const { getCancellations, getCourtesies } = useOrder();
  const { getVoidedPayments, getDiscountedPayments } = usePayment();
  usePrintOrientation("portrait");

  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    setData(null);
    setError(false);
    Promise.all([
      getCancellations(dateFrom, dateTo),
      getVoidedPayments(dateFrom, dateTo),
      getDiscountedPayments(dateFrom, dateTo),
      getCourtesies(dateFrom, dateTo),
    ])
      .then(([cancellations, voided, discounted, courtesies]) =>
        setData({ cancellations, voided, discounted, courtesies })
      )
      .catch(() => setError(true));
  }, [dateFrom, dateTo]);

  const dateRangeLabel = `Del ${moment(dateFrom).format("DD/MM/YYYY")} al ${moment(
    dateTo
  ).format("DD/MM/YYYY")}`;

  // Resumen por persona: quién cancela/anula más. "Liberar mesa" también
  // cancela (motivo "Mesa liberada sin preparar"), a nombre de quien la liberó.
  const byUser = data
    ? orderBy(
        map(
          groupBy(
            [
              ...map(data.cancellations, (row) => ({
                name: row.cancelled_by_name || "—",
                amount: price(row),
                kind: "cancelled",
              })),
              ...map(data.voided, (row) => ({
                name: row.voided_by_name || "—",
                amount: Number(row.totalPayment || 0),
                kind: "voided",
              })),
              ...map(data.discounted, (row) => ({
                name: row.discount_by_name || "—",
                amount: Number(row.discount_amount || 0),
                kind: "discount",
              })),
              ...map(data.courtesies, (row) => ({
                name: row.courtesy_by_name || "—",
                amount: Number(row.courtesy_value || 0),
                kind: "discount",
              })),
            ],
            "name"
          ),
          (rows, name) => ({
            name,
            cancelled: rows.filter((r) => r.kind === "cancelled").length,
            voided: rows.filter((r) => r.kind === "voided").length,
            discounts: rows.filter((r) => r.kind === "discount").length,
            total: sumBy(rows, "amount"),
          })
        ),
        ["total"],
        ["desc"]
      )
    : [];

  const byReason = data
    ? orderBy(
        map(groupBy(data.cancellations, "reason_label"), (rows, label) => ({
          label,
          count: rows.length,
          total: sumBy(rows, price),
        })),
        ["count"],
        ["desc"]
      )
    : [];

  return (
    <>
      <div className="screen-only">
        <HeaderPage
          title="Anulaciones y descuentos"
          btnTitle={
            <>
              <Icon name="print" /> Imprimir
            </>
          }
          btnClick={() => window.print()}
        />
      </div>

      <div
        className="screen-only"
        style={{ display: "flex", gap: "15px", margin: "10px 0 20px" }}
      >
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Desde</label>
          <input
            type="date"
            value={dateFrom}
            max={dateTo}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: "block", marginBottom: 4 }}>Hasta</label>
          <input
            type="date"
            value={dateTo}
            min={dateFrom}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
      </div>

      <PrintTable>
        <PrintHeader title="Anulaciones y descuentos" subtitle={dateRangeLabel} />

        {error ? (
          <Message negative>No se pudieron cargar las anulaciones.</Message>
        ) : !data ? (
          <Loader active inline="centered">
            Cargando...
          </Loader>
        ) : size(data.cancellations) === 0 &&
          size(data.voided) === 0 &&
          size(data.discounted) === 0 &&
          size(data.courtesies) === 0 ? (
          <div className="print-content">
            <p>No hay pedidos cancelados, ventas anuladas ni descuentos en este rango.</p>
          </div>
        ) : (
          <div className="print-content">
            <Statistic.Group widths="three" size="small" style={{ margin: "10px 0 30px" }}>
              <Statistic color="orange">
                <Statistic.Value>{size(data.cancellations)}</Statistic.Value>
                <Statistic.Label>
                  Pedidos cancelados · {money(sumBy(data.cancellations, price))}
                </Statistic.Label>
              </Statistic>
              <Statistic color="red">
                <Statistic.Value>{size(data.voided)}</Statistic.Value>
                <Statistic.Label>
                  Ventas anuladas ·{" "}
                  {money(sumBy(data.voided, (row) => Number(row.totalPayment)))}
                </Statistic.Label>
              </Statistic>
              <Statistic color="yellow">
                <Statistic.Value>
                  {size(data.discounted) + size(data.courtesies)}
                </Statistic.Value>
                <Statistic.Label>
                  Descuentos y cortesías ·{" "}
                  {money(
                    sumBy(data.discounted, (row) => Number(row.discount_amount)) +
                      sumBy(data.courtesies, (row) => Number(row.courtesy_value || 0))
                  )}
                </Statistic.Label>
              </Statistic>
            </Statistic.Group>

            <h4>Por persona</h4>
            <Table compact unstackable celled style={{ marginBottom: 30 }}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Quién</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Pedidos cancelados</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Ventas anuladas</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Descuentos</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Total (S/)</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {map(byUser, (row) => (
                  <Table.Row key={row.name}>
                    <Table.Cell>{row.name}</Table.Cell>
                    <Table.Cell textAlign="right">{row.cancelled}</Table.Cell>
                    <Table.Cell textAlign="right">{row.voided}</Table.Cell>
                    <Table.Cell textAlign="right">{row.discounts}</Table.Cell>
                    <Table.Cell textAlign="right">{row.total.toFixed(2)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>

            {size(data.cancellations) > 0 && (
              <>
                <h4>Pedidos cancelados — por motivo</h4>
                <Table compact unstackable celled style={{ marginBottom: 20 }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Motivo</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">Cantidad</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">Total (S/)</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {map(byReason, (row) => (
                      <Table.Row key={row.label}>
                        <Table.Cell>{row.label}</Table.Cell>
                        <Table.Cell textAlign="right">{row.count}</Table.Cell>
                        <Table.Cell textAlign="right">{row.total.toFixed(2)}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>

                <h4>Pedidos cancelados — detalle</h4>
                <Table compact unstackable celled style={{ marginBottom: 30 }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Cancelado</Table.HeaderCell>
                      <Table.HeaderCell>Mesa</Table.HeaderCell>
                      <Table.HeaderCell>Plato</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">S/</Table.HeaderCell>
                      <Table.HeaderCell>Motivo</Table.HeaderCell>
                      <Table.HeaderCell>Canceló</Table.HeaderCell>
                      <Table.HeaderCell>Lo registró</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {map(data.cancellations, (row) => (
                      <Table.Row key={row.id}>
                        <Table.Cell>{when(row.cancelled_at)}</Table.Cell>
                        <Table.Cell>{row.table_place ? getTableLabel(row) : "—"}</Table.Cell>
                        <Table.Cell>{row.product_title || "Producto eliminado"}</Table.Cell>
                        <Table.Cell textAlign="right">{price(row).toFixed(2)}</Table.Cell>
                        <Table.Cell>
                          {row.reason_label}
                          {row.note && ` — ${row.note}`}
                        </Table.Cell>
                        <Table.Cell>{row.cancelled_by_name || "—"}</Table.Cell>
                        <Table.Cell>{row.order_created_by_name || "Autoservicio (QR)"}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </>
            )}

            {size(data.courtesies) > 0 && (
              <>
                <h4>Platos de cortesía</h4>
                <Table compact unstackable celled style={{ marginBottom: 30 }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Fecha</Table.HeaderCell>
                      <Table.HeaderCell>Mesa</Table.HeaderCell>
                      <Table.HeaderCell>Plato</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">Valor (S/)</Table.HeaderCell>
                      <Table.HeaderCell>Motivo</Table.HeaderCell>
                      <Table.HeaderCell>Dio la cortesía</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {map(data.courtesies, (row) => (
                      <Table.Row key={row.id}>
                        <Table.Cell>{when(row.created_at)}</Table.Cell>
                        <Table.Cell>{getTableLabel(row)}</Table.Cell>
                        <Table.Cell>{row.product_data?.title || "Producto eliminado"}</Table.Cell>
                        <Table.Cell textAlign="right">
                          {Number(row.courtesy_value || 0).toFixed(2)}
                        </Table.Cell>
                        <Table.Cell>{row.courtesy_reason}</Table.Cell>
                        <Table.Cell>{row.courtesy_by_name || "—"}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </>
            )}

            {size(data.discounted) > 0 && (
              <>
                <h4>Descuentos y cortesías</h4>
                <Table compact unstackable celled style={{ marginBottom: 30 }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Aplicado</Table.HeaderCell>
                      <Table.HeaderCell>Mesa</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">Consumo (S/)</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">Descuento (S/)</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">Cobrado (S/)</Table.HeaderCell>
                      <Table.HeaderCell>Motivo</Table.HeaderCell>
                      <Table.HeaderCell>Aplicó</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {map(data.discounted, (row) => (
                      <Table.Row key={row.id}>
                        <Table.Cell>{when(row.discounted_at || row.paid_at)}</Table.Cell>
                        <Table.Cell>{getTableLabel(row)}</Table.Cell>
                        <Table.Cell textAlign="right">
                          {Number(row.subtotal).toFixed(2)}
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                          {Number(row.discount_amount).toFixed(2)}
                          {row.discount_type === "PERCENT" &&
                            ` (${Number(row.discount_value)}%)`}
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                          {Number(row.totalPayment).toFixed(2)}
                        </Table.Cell>
                        <Table.Cell>{row.discount_reason}</Table.Cell>
                        <Table.Cell>{row.discount_by_name || "—"}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </>
            )}

            {size(data.voided) > 0 && (
              <>
                <h4>Ventas anuladas</h4>
                <Table compact unstackable celled style={{ marginBottom: 30 }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Anulada</Table.HeaderCell>
                      <Table.HeaderCell>Mesa</Table.HeaderCell>
                      <Table.HeaderCell textAlign="right">S/</Table.HeaderCell>
                      <Table.HeaderCell>Forma de pago</Table.HeaderCell>
                      <Table.HeaderCell>Cobrada</Table.HeaderCell>
                      <Table.HeaderCell>Motivo</Table.HeaderCell>
                      <Table.HeaderCell>Anuló</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {map(data.voided, (row) => (
                      <Table.Row key={row.id}>
                        <Table.Cell>{when(row.voided_at)}</Table.Cell>
                        <Table.Cell>{getTableLabel(row)}</Table.Cell>
                        <Table.Cell textAlign="right">
                          {Number(row.totalPayment).toFixed(2)}
                        </Table.Cell>
                        <Table.Cell>{getPaymentMethod(row.paymentType)?.text}</Table.Cell>
                        <Table.Cell>{when(row.paid_at)}</Table.Cell>
                        <Table.Cell>{row.void_reason}</Table.Cell>
                        <Table.Cell>{row.voided_by_name || "—"}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </>
            )}
          </div>
        )}
      </PrintTable>
    </>
  );
}
